import { IAddressInputAddress } from '@opendoor/cinderblocks/growth/AddressInput/AddressInput';

import 'isomorphic-unfetch';

const defaultCache: apiCache = {
  get(_key: string) {
    return undefined;
  },
  set(_key: string, _value: any, _time: number) {
    return;
  },
};

interface fetchWithRetryOptions extends RequestInit {
  retries?: number;
  retryDelay?: number; // in milliseconds
}
export interface PaginationRequest {
  after?: string;
  before?: string;
  first?: number;
  last?: number;
}

/**
 * A wrapper around `fetch` with optional retries.
 * Retries up to `options.retries` times (defaults to `5` if not specified)
 * with a delay of `options.retryDelay` (defaults to `500ms` if not specified)
 */
const fetchWithRetry = async (url: string, options: fetchWithRetryOptions): Promise<Response> => {
  const retries = options.retries || 0;
  const retryDelay = options.retryDelay || 500;

  try {
    return await fetch(url, options);
  } catch (err) {
    if (retries < 1) throw err;

    // sleep the specified amount of time, then recursively retry
    await new Promise((r) => setTimeout(r, retryDelay));
    return await fetchWithRetry(url, { ...options, retries: retries - 1 });
  }
};

import * as Cookies from 'js-cookie';

import { RAILS_URL, XSRF_COOKIE_KEY, PARTNERSHIP_BFF_URL, ATHENA_URL } from '../globals';
import { Sentry } from '@opendoor/observability';
import { getAnonymousId } from '../shared/trackers';
import { apiCache } from '../../declarations/shared';
import { GraphQLClient } from 'graphql-request';
import { getSdk as getAthenaSdk, PageInput, PropertyFilters } from '../../__generated__/athena';
import { getSdk as getPartnershipsSdk } from '../../__generated__/partnerships';

import { getActiveSellDirectMarketOptions } from '../../helpers/markets';
import { getLocalFeatureFlagOverrides } from '../helpers/hooks';

interface IPostAddressOptions {
  agent_request?: boolean;
  channel?: string;
  covid19Whitelist?: boolean;
  experiment_entity_id?: string;
  isTradeInDashboard?: boolean;
  manual?: boolean;
  seller_flow_name_override?: string;
  seller_flow_uuid_override?: string;
  trackingKeywords?: string;
}

export function doBFFPost(
  apiUrl: string,
  jsonParams: object,
  extraHeaders?: object,
): Promise<Response> {
  return fetchWithRetry(apiUrl, {
    retries: 5,
    retryDelay: 500,
    credentials: 'include',
    method: 'POST',
    body: JSON.stringify(jsonParams),
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-CSRF-Token': Cookies.get(XSRF_COOKIE_KEY) || '',
      ...extraHeaders,
    },
  });
}

export function doBFFPatch(
  apiUrl: string,
  jsonParams: object,
  extraHeaders?: object,
): Promise<Response> {
  return fetchWithRetry(apiUrl, {
    retries: 5,
    retryDelay: 500,
    credentials: 'include',
    method: 'PATCH',
    body: JSON.stringify(jsonParams),
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-CSRF-Token': Cookies.get(XSRF_COOKIE_KEY) || '',
      ...extraHeaders,
    },
  });
}

export function getAthenaClient() {
  return getAthenaSdk(
    new GraphQLClient(ATHENA_URL, {
      credentials: 'include',
      headers: {
        'apollographql-client-name': 'cosmos',
      },
    }),
  );
}

function getPartnershipsClient() {
  return getPartnershipsSdk(
    new GraphQLClient(`${PARTNERSHIP_BFF_URL}/api/internal/graphql`, {
      credentials: 'include',
      headers: {
        'apollographql-client-name': 'cosmos',
      },
    }),
  );
}

export function doGet(path: string): Promise<Response> {
  return fetchWithRetry(`${RAILS_URL}${path}`, {
    retries: 5,
    retryDelay: 500,
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-CSRF-Token': Cookies.get(XSRF_COOKIE_KEY) || '',
    },
  });
}

export function trackLead(trackingKeywords: string) {
  return doGet(`/track/${trackingKeywords}`);
}

export type Lead = {
  state: string;
  id: string;
  url: string;
  address: {
    street: string;
  };
  readable_state: string;
};
export async function getLeads(): Promise<Array<Lead>> {
  const response = await doGet('/api/v1/seller_leads');
  return await response.json();
}

type InfoResponse = {
  isLoggedIn: boolean;
  human?: {
    has_resale_closing_dashboard?: boolean;
  };
};
export async function getSessionExtras(): Promise<{ has_resale_closing_dashboard: boolean }> {
  try {
    const response = await doGet('/session/info');
    const data = (await response.json()) as InfoResponse;
    return { has_resale_closing_dashboard: data.human?.has_resale_closing_dashboard ?? false };
  } catch {
    return { has_resale_closing_dashboard: false };
  }
}

export function requestDevAPIAccess(
  humanId: number,
  name: string,
  email: string,
  company: string,
  blurb: string,
) {
  return doBFFPost(`${PARTNERSHIP_BFF_URL}/api/notification`, {
    humanId,
    name,
    email,
    company,
    blurb,
  }).then(handleResponse);
}

export async function validatePartnerEmailAddress(email: string) {
  try {
    return getPartnershipsClient().ValidateEmail({ email });
  } catch (e) {
    Sentry.captureException(e);
    return;
  }
}

export const createSellerInput = async (
  { street1, unit, city, postal_code, state }: IAddressInputAddress,
  {
    channel,
    covid19Whitelist,
    experiment_entity_id: experimentEntityId,
    isTradeInDashboard,
    seller_flow_name_override: sellerFlowNameOverride,
    seller_flow_uuid_override: sellerFlowUuidOverride,
    trackingKeywords,
  }: IPostAddressOptions = {},
) => {
  if (trackingKeywords) {
    await trackLead(trackingKeywords).catch((e) => Sentry.captureException(e));
  }

  return doBFFPost(
    ATHENA_URL,
    {
      operationName: 'CreateSellerInputWithTrackingDataFromCookies',
      query: `mutation CreateSellerInputWithTrackingDataFromCookies(
        $input: OdProtosReceptionServices_CreateSellerInputRequestInput!
      ) {
        reception {
          createSellerInputWithTrackingDataFromCookies(input: $input) {
            sellerInput {
              uuid
              pdpUrl
              address {
                postalCode
              }
            }
          }
        }
      }`,
      variables: {
        input: {
          address: {
            city,
            postalCode: postal_code,
            state,
            street: street1,
            unit,
          },
          channel,
          experimentEntityId,
          sellerFlowNameOverride,
          sellerFlowUuidOverride,
          isTradeInDashboard,
          covid19Whitelist,
        },
      },
    },
    { 'apollographql-client-name': 'cosmos' },
  ).then(handleResponse);
};

function handleResponse(res: Response) {
  if (res.ok) {
    return res.json();
  }
  Sentry.captureException(res);
  return;
}

export function getFeatureFlag(name: string, market?: string) {
  const experimentEntityId = getAnonymousId();

  // Use local overrides if available
  const shouldOverrideLocally = getLocalFeatureFlagOverrides(name);
  if (shouldOverrideLocally) {
    Promise.resolve(shouldOverrideLocally);
  }

  return (
    doBFFPost(
      ATHENA_URL,
      {
        operationName: 'GetFeatureFlagQuery',
        query: `query GetFeatureFlagQuery($input: FeatureFlagsInput!) {
        external{
          optimizely{
            featureFlags(input:$input){
              enabled
            }
          }
        }
      }`,
        variables: {
          input: {
            experimentEntityId,
            experimentNames: [name],
            market: market ?? '',
          },
        },
        skip: !experimentEntityId,
      },
      { 'apollographql-client-name': 'cosmos' },
    )
      // sometimes we skip this query if experimentEntityId does not exist, used custom handler below
      // so we don't sentry when the query isn't executed
      .then((res) => {
        if (res.status) {
          return res.json();
        }
        return;
      })
      .catch((e) => Sentry.captureException(e))
  );
}

export function getLatestLeadQuery(customerUUID: string, addressToken: string) {
  return doBFFPost(
    ATHENA_URL,
    {
      operationName: 'GetLatestLeadQuery',
      query: `query GetLatestLeadQuery($addressToken: String!, $customerUUID: String!) {
        seller {
          getLatestLead(
            input: { addressUuid: $addressToken, customerUuid: $customerUUID }
          ) {
            lead {
              source {
                id
              }
            }
          }
        }
      }`,
      variables: {
        customerUUID,
        addressToken,
      },
    },
    { 'apollographql-client-name': 'cosmos' },
  )
    .then(handleResponse)
    .catch((e) => Sentry.captureException(e));
}

export async function getPropertySchoolsQuery(latitude: number, longitude: number) {
  try {
    return getAthenaClient().GetNicheSchools({ latitude, longitude });
  } catch (e) {
    Sentry.captureException(e);
    return;
  }
}

export async function getBuyerAgentQuery(paramsId: string) {
  try {
    return getAthenaClient().GetAgentByParamsId({ paramsId });
  } catch (e) {
    Sentry.captureException(e);
    return;
  }
}

export async function getAgentRatingsQuery(agentId: string, paginationRequest?: PaginationRequest) {
  try {
    return getAthenaClient().GetAgentRatings({ agentId, paginationRequest });
  } catch (e) {
    Sentry.captureException(e);
    return;
  }
}

export async function getAgentProfileInfoById(agentId: string) {
  try {
    return getAthenaClient().GetAgentProfileInfo({ agentId });
  } catch (e) {
    Sentry.captureException(e);
    return;
  }
}

export function fetchAdressesByCustomerUUID(customerUUID: string) {
  return doBFFPost(
    ATHENA_URL,
    {
      operationName: 'FetchAdressesQuery',
      query: `query FetchAdressesQuery($customerUUID: String!) {
        sellDirect {
          getAddressesByCustomerUUID(input: { customerUuid: $customerUUID }) {
            addressToken
          }
        }
      }`,
      variables: {
        customerUUID,
      },
    },
    { 'apollographql-client-name': 'cosmos' },
  )
    .then(handleResponse)
    .catch((e) => Sentry.captureException(e));
}

// TODO: migrate to use sell direct getLead query and deprecate web lead query (from the ReturnExperienceQuery)
export function fetchSellerReturnExperience(sellerInputUUID: string) {
  return doBFFPost(
    ATHENA_URL,
    {
      operationName: 'ReturnExperienceQuery',
      query: `query ReturnExperienceQuery($sellerInputUUID: String!) {
      reception {
        getSellerInput(input: { uuid: $sellerInputUUID }) {
          sellerInput {
            productOfferings {
              deniedAt
            }
            address {
              street
            }
            answers {
              email
            }
          }
        }
      }
      web {
        lead(leadToken: $sellerInputUUID) {
          customer {
            id
            firstName
          }
          address {
            address_token
            street
          }
          offer {
            assessment_scheduling_expires_at
            offer_request_source
            state
            recorded_price_cents
          }
          previousOffer {
            headlinePrice
          }
        }
      }
      sellDirect {
        getLead(input: { lead: { source: { type: RECEPTION, id: $sellerInputUUID }} }) {
          lead {
            isQualified
          }
        }
      }
    }`,
      variables: {
        sellerInputUUID,
      },
    },
    { 'apollographql-client-name': 'cosmos' },
  )
    .then(handleResponse)
    .catch((e) => Sentry.captureException(e));
}

export function refreshOffer(sellerInputUUID: string) {
  return doBFFPost(
    ATHENA_URL,
    {
      operationName: 'RequestRefreshOfferMutation',
      query: `mutation RequestRefreshOfferMutation($sellerInputUUID: String!) {
      seller {
        refreshOffersSingleLead(
          input: {
            leadId: { lead: { source: {type: RECEPTION, id: $sellerInputUUID} } }
            offerRequestSource: HOMEPAGE_RETURN_EXPERIENCE
          }
        ) {
          __typename
        }
      }
    }`,
      variables: {
        sellerInputUUID,
      },
    },
    { 'apollographql-client-name': 'cosmos' },
  )
    .then(handleResponse)
    .catch((e) => Sentry.captureException(e));
}

export function standardizeAddress(address: IAddressInputAddress) {
  return doBFFPost(
    ATHENA_URL,
    {
      operationName: 'StandardizeAddress',
      query: `query StandardizeAddress($address: OdProtosCasaServices_CasaRequest_FindByFieldsInput!) {
      casa {
        findByFields(input: $address) {
          address {
            uuid,
            street,
            city,
            state,
            unitNumber,
            unitType
            postalCode,
            latitude,
            longitude
          }
        }
      }
    }`,
      variables: {
        address: {
          city: address.city,
          postalCode: address.postal_code,
          state: address.state,
          street: address.street1,
          unitNumber: address.unit,
        },
      },
    },
    { 'apollographql-client-name': 'cosmos' },
  )
    .then(handleResponse)
    .catch((e) => Sentry.captureException(e));
}

export function unsubscribeMailer(addressUuid: string) {
  return doBFFPost(
    ATHENA_URL,
    {
      operationName: 'UnsubscribeMailer',
      query: `mutation UnsubscribeMailer($address: OdProtosWebServicesSellDirect_UnsubscribeMailerRequestInput!) {
      sellDirect {
        unsubscribeMailer(input: $address) {
          _empty
        }
      }
    }`,
      variables: {
        address: {
          addressToken: addressUuid,
        },
      },
    },
    { 'apollographql-client-name': 'cosmos' },
  )
    .then(handleResponse)
    .catch((e) => Sentry.captureException(e));
}

export function requestNewToken(expiredToken: string) {
  return doBFFPost(
    ATHENA_URL,
    {
      operationName: 'RequestNewToken',
      query: `mutation RequestNewToken($expiredToken: String!) {
        passwordless {
          requestNewToken(input: {token: $expiredToken }) {
            token
          }
        }
      }`,
      variables: { expiredToken },
    },
    { 'apollographql-client-name': 'cosmos' },
  )
    .then(handleResponse)
    .catch((e) => Sentry.captureException(e));
}

export function requestNewTokenByEmail(email: string, redirectUrl?: string) {
  return doBFFPost(
    ATHENA_URL,
    {
      operationName: 'RequestNewToken',
      query: `mutation RequestNewToken($email: String!, $redirectUrl: String) {
        passwordless {
          requestNewToken(input: {email: $email, redirectUrl: $redirectUrl}) {
            token
          }
        }
      }`,
      variables: { email, redirectUrl },
    },
    { 'apollographql-client-name': 'cosmos' },
  )
    .then(handleResponse)
    .catch((e) => Sentry.captureException(e));
}

export async function fetchActiveMarkets(cache = defaultCache) {
  // we cache the results of this endpoint because it tends to time out
  let activeMarkets = cache.get('markets');

  if (activeMarkets === undefined) {
    const listMarketsResponse = await doBFFPost(
      ATHENA_URL,
      {
        operationName: 'ListMarkets',
        query: `query ListMarkets {
          webMarket {
            listMarkets(
              input:{}
            ) {
              market {
                id
                marketData {
                  marketIdentifier
                  active
                  displayName
                  launchDate
                  homesPath
                }
              }
            }
          }
        }`,
      },
      { 'apollographql-client-name': 'cosmos' },
    );

    const data = await handleResponse(listMarketsResponse);

    const allMarkets = data.data.webMarket.listMarkets.market;

    activeMarkets = getActiveSellDirectMarketOptions(allMarkets);
    cache.set('markets', activeMarkets, 6 * 60 * 60); // 6 hours
  }

  return activeMarkets;
}

export async function getPropertiesByMarketQuery(
  shape: string,
  filters: PropertyFilters,
  pageInput: PageInput,
) {
  try {
    return await getAthenaClient().GetPropertiesByMarket({ shape, filters, pageInput });
  } catch (e) {
    Sentry.captureException(e);
    return;
  }
}
